import React, { useMemo } from 'react';
import { CompleteKundali, Language, PlanetName, PlanetPosition } from '../../types/kundali';
import { ZODIAC_HI } from '../../utils/language';
import { getSavBySign, getBavBySign } from '../../utils/vedicEngine';
import { 
  ShieldCheck, 
  Sparkles, 
  Layers, 
  HelpCircle, 
  FileCheck2, 
  BookOpen, 
  Info,
  MapPin,
  ChevronRight,
  Eye,
  AlertCircle
} from 'lucide-react';

export interface AshtakavargaGocharInspectorProps {
  kundali: CompleteKundali;
  selectedPlanet: PlanetName;
  selectedHouse: number; // 1 to 12
  selectedPerspective?: 'lagna' | 'moon' | 'sun';
  lang?: Language;
  onOpenEvidenceDrawer?: (planet?: PlanetName) => void;
  transitPlanets?: PlanetPosition[];
}

export const AshtakavargaGocharInspector: React.FC<AshtakavargaGocharInspectorProps> = ({
  kundali,
  selectedPlanet,
  selectedHouse,
  selectedPerspective = 'lagna',
  lang = 'en',
  onOpenEvidenceDrawer,
  transitPlanets = []
}) => {
  const isHi = lang === 'hi';

  const { houses, ashtakvarga } = kundali;
  const rawSAV = ashtakvarga?.sarvashtakvarga || [];
  const rawBAV = ashtakvarga?.bhinna || {};

  // Reference Sign Index
  const lagnaHouse = houses[0];
  const lagnaSignIndex = lagnaHouse ? lagnaHouse.signIndex : 0;
  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const moonSignIndex = moonPlanet ? moonPlanet.signIndex : 0;
  const sunPlanet = kundali.planets.find(p => p.name === 'Sun');
  const sunSignIndex = sunPlanet ? sunPlanet.signIndex : 0;

  const referenceSignIndex = useMemo(() => {
    if (selectedPerspective === 'moon') return moonSignIndex;
    if (selectedPerspective === 'sun') return sunSignIndex;
    return lagnaSignIndex;
  }, [selectedPerspective, moonSignIndex, sunSignIndex, lagnaSignIndex]);

  // Actual zodiac sign index corresponding to selectedHouse in the chosen perspective
  const targetSignIndex = (referenceSignIndex + (selectedHouse - 1)) % 12;
  const targetHouseObj = houses.find(h => h.signIndex === targetSignIndex) || houses[selectedHouse - 1] || { houseNumber: selectedHouse, sign: 'Aries' };
  const targetSignName = targetHouseObj.sign;
  const natalLagnaHouseNumber = ((targetSignIndex - lagnaSignIndex + 12) % 12) + 1;

  // Authoritative SAV value for this sign (canonical Sign-Indexed)
  const savScore = getSavBySign(ashtakvarga, targetSignIndex);

  // Total SAV points across chart (must be strictly 337)
  const savTotal = ashtakvarga?.sarvashtakvarga?.reduce((sum, val) => sum + val, 0) ?? 337;

  // Authoritative BAV value for 7 physical planets
  const isSevenGraha = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].includes(selectedPlanet);
  const bavScore = isSevenGraha
    ? getBavBySign(ashtakvarga, selectedPlanet, targetSignIndex)
    : null;

  // Transit coordinate info for selected planet
  const tp = transitPlanets.find(p => p.name === selectedPlanet);

  return (
    <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl p-4 sm:p-5 shadow-xs space-y-4 text-xs text-stone-800 dark:text-stone-200">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#B58A4A]/20 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-[#174C4B]/10 border border-[#174C4B]/20 flex items-center justify-center text-[#174C4B]">
            <ShieldCheck className="w-4.5 h-4.5 text-[#B58A4A]" />
          </div>
          <div>
            <h4 className="font-serif font-bold text-sm sm:text-base text-[#174C4B]">
              {isHi ? 'अष्टकवर्ग गोचर संबल एवं शास्त्रीय मॉड्युलेशन' : 'Ashtakavarga Gochar Modulation & Context Inspector'}
            </h4>
            <p className="text-[11px] text-stone-500">
              {isHi 
                ? 'भिन्नाष्टकवर्ग (BAV) एवं सर्वाष्टकवर्ग (SAV) शास्त्रीय साक्ष्य विश्लेषण'
                : 'Bhinna Ashtakavarga (BAV) & Sarvashtakavarga (SAV) Classical Modulation Context'}
            </p>
          </div>
        </div>

        {onOpenEvidenceDrawer && (
          <button
            onClick={() => onOpenEvidenceDrawer(selectedPlanet)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#174C4B] hover:bg-[#174C4B]/90 text-white font-semibold transition-colors shadow-2xs text-[11px] self-start sm:self-auto"
          >
            <FileCheck2 className="w-3.5 h-3.5" />
            <span>{isHi ? 'विस्तृत प्रमाण देखें' : 'View Full Evidence'}</span>
          </button>
        )}
      </div>

      {/* Target Focus Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {/* Planet Card */}
        <div className="bg-white border border-stone-200/80 rounded-xl p-3 space-y-1">
          <span className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold block">
            {isHi ? 'गोचर ग्रह' : 'Transit Graha'}
          </span>
          <div className="font-bold text-sm text-[#174C4B] flex items-center gap-1.5">
            <span>{selectedPlanet}</span>
            {tp?.isRetrograde && (
              <span className="text-[9px] px-1 rounded bg-rose-50 text-rose-700 border border-rose-200 font-mono">℞</span>
            )}
          </div>
          <span className="text-[10.5px] text-stone-500 font-mono block">
            {tp ? `${tp.sign} ${tp.degreeFormatted}` : 'Active Position'}
          </span>
        </div>

        {/* House & Sign Card */}
        <div className="bg-white border border-stone-200/80 rounded-xl p-3 space-y-1">
          <span className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold block">
            {isHi ? 'निरीक्षित भाव व राशि' : 'Inspected Bhava & Rashi'}
          </span>
          <div className="font-bold text-sm text-stone-800">
            {isHi ? `भाव ${selectedHouse}` : `House ${selectedHouse}`} ({targetSignName})
          </div>
          <span className="text-[10.5px] text-stone-500 font-mono block">
            Natal H{natalLagnaHouseNumber} (D1 Lagna)
          </span>
        </div>

        {/* BAV Bindu Card */}
        <div className="bg-white border border-[#B58A4A]/40 rounded-xl p-3 space-y-1 shadow-2xs">
          <span className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold block">
            {isHi ? 'भिन्नाष्टकवर्ग (BAV)' : 'Bhinna Ashtakavarga (BAV)'}
          </span>
          <div className="font-bold text-sm text-[#174C4B] flex items-center gap-1">
            {isSevenGraha ? (
              <>
                <span className="text-base font-mono">{bavScore}</span>
                <span className="text-stone-400 font-normal text-xs">/ 8 Bindus</span>
              </>
            ) : (
              <span className="text-[11px] text-stone-600 font-medium">N/A (7 Grahas Only)</span>
            )}
          </div>
          <span className="text-[10px] text-stone-500 block truncate">
            {isSevenGraha ? `${selectedPlanet}'s personal contribution` : 'Lunar node (SAV applicable)'}
          </span>
        </div>

        {/* SAV Total Card */}
        <div className="bg-white border border-[#B58A4A]/40 rounded-xl p-3 space-y-1 shadow-2xs">
          <span className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold block">
            {isHi ? 'सर्वाष्टकवर्ग (SAV)' : 'Sarvashtakavarga (SAV)'}
          </span>
          <div className="font-bold text-sm text-[#174C4B] flex items-center gap-1">
            <span className="text-base font-mono">{savScore}</span>
            <span className="text-stone-400 font-normal text-xs">/ 337 total</span>
          </div>
          <span className="text-[10px] text-stone-500 block">
            Baseline average: 28.08 bindus
          </span>
        </div>
      </div>

      {/* 12-House SAV Distribution Mini-Rail */}
      <div className="bg-white border border-stone-200/80 rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between text-[11px]">
          <span className="font-bold text-[#174C4B] flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-[#B58A4A]" />
            <span>{isHi ? 'सर्वाष्टकवर्ग भाव वितरण (12 Houses SAV)' : 'Sarvashtakavarga 12-House Environmental Buffer'}</span>
          </span>
          <span className="font-mono text-stone-500 text-[10px]">
            {isHi ? `कुल बिंदु: ${savTotal}` : `SAV Total: ${savTotal} Bindus`}
          </span>
        </div>

        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
          {Array.from({ length: 12 }).map((_, idx) => {
            const hNum = idx + 1;
            const points = rawSAV[idx] ?? 28;
            const isTarget = hNum === natalLagnaHouseNumber;

            return (
              <div
                key={hNum}
                className={`p-1.5 rounded-lg border text-center transition-all ${
                  isTarget
                    ? 'bg-amber-50 border-[#B58A4A] ring-1 ring-[#B58A4A] shadow-2xs'
                    : 'bg-stone-50/70 border-stone-200/70'
                }`}
              >
                <span className={`text-[9.5px] font-bold block ${isTarget ? 'text-[#174C4B]' : 'text-stone-500'}`}>
                  H{hNum}
                </span>
                <span className={`font-mono text-xs font-bold block ${isTarget ? 'text-[#174C4B]' : 'text-stone-700'}`}>
                  {points}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Epistemic 6-Part Evidence Breakdown */}
      <div className="bg-white border border-stone-200/80 rounded-xl p-3.5 space-y-3">
        <div className="flex items-center gap-2 border-b border-stone-100 pb-2">
          <BookOpen className="w-4 h-4 text-[#B58A4A]" />
          <h5 className="font-bold text-xs uppercase tracking-wider text-[#174C4B]">
            {isHi ? 'शास्त्रीय अष्टकवर्ग साक्ष्य विवरण (6-Part Epistemic Grounding)' : 'Ashtakavarga Provenance & Classical Modulation Grounding'}
          </h5>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] leading-relaxed">
          {/* Fact & Rule Column */}
          <div className="space-y-2.5">
            <div className="p-2.5 bg-stone-50 rounded-lg border border-stone-200/80">
              <strong className="text-[#174C4B] block text-[10.5px] uppercase tracking-wide mb-0.5">
                1. Calculated Fact (गणना तथ्य)
              </strong>
              <p className="text-stone-600">
                • {selectedPlanet} in Natal House {natalLagnaHouseNumber} ({targetSignName})<br />
                {isSevenGraha ? (
                  <>• BAV ({selectedPlanet}): <strong>{bavScore} Bindus</strong> (out of 8 max)<br /></>
                ) : (
                  <>• BAV: Evaluated for 7 physical Grahas (BPHS Ch. 66–72)<br /></>
                )}
                • SAV: <strong>{savScore} Bindus</strong> (out of {savTotal} total)
              </p>
            </div>

            <div className="p-2.5 bg-stone-50 rounded-lg border border-stone-200/80">
              <strong className="text-[#174C4B] block text-[10.5px] uppercase tracking-wide mb-0.5">
                2. Classical Rule (शास्त्रीय नियम)
              </strong>
              <p className="text-stone-600">
                <em>Brihat Parashara Hora Shastra (BPHS Ch. 66–73)</em>: Bhinna Ashtakavarga evaluates the individual planet's auspicious contribution, while Sarvashtakavarga assesses the collective environmental cushion of the rashi.
              </p>
            </div>
          </div>

          {/* Interpretation & Uncertainty Column */}
          <div className="space-y-2.5">
            <div className="p-2.5 bg-stone-50 rounded-lg border border-stone-200/80">
              <strong className="text-[#174C4B] block text-[10.5px] uppercase tracking-wide mb-0.5">
                3. Classical Interpretation (फलित संदर्भ)
              </strong>
              <p className="text-stone-600">
                {isSevenGraha ? (
                  bavScore !== null && bavScore >= 4 ? (
                    `In ${targetSignName}, ${selectedPlanet} possesses ${bavScore} BAV bindus, providing individual planetary agency. The house environment carries ${savScore} SAV bindus.`
                  ) : (
                    `In ${targetSignName}, ${selectedPlanet} carries ${bavScore} BAV bindus against a baseline of 4. The house environment carries ${savScore} SAV bindus.`
                  )
                ) : (
                  `Rahu and Ketu operate through the collective environmental support (${savScore} SAV bindus) of ${targetSignName}.`
                )}
              </p>
            </div>

            <div className="p-2.5 bg-stone-50 rounded-lg border border-stone-200/80">
              <strong className="text-[#174C4B] block text-[10.5px] uppercase tracking-wide mb-0.5">
                4. Uncertainty & Acharya Pause (अनिश्चितता)
              </strong>
              <p className="text-stone-600">
                Natal Ashtakavarga bindus are static to the birth chart geometry and do not mutate over time. Transits trigger the activation of these baseline strengths.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
